import { notification } from "antd";
import cuid from "cuid";
import { find, get, intersection, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { BUILD_GENERAL_COSTS } from "utils/api/graphql/mutations/costs";
import { UPDATE_PROJECT } from "utils/api/graphql/mutations/projects";
import {
  CREATE_SUBSCRIPTIONS,
  UPDATE_SUBSCRIPTION,
} from "utils/api/graphql/mutations/subscription";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import {
  MODAL_STATES,
  PROCESS_TYPES,
  SCREENS,
  SUBSCRIPTION_STATUSES,
  TEMPLATE_TYPES,
  TEMPLATES,
} from "utils/constants";
import { downloadFiles } from "utils/helpers/files";
import { buildRetroactivityPlanFilters } from "../retroactivity";
import ExpirationModal from "../Widgets/ExpirationModal";
import View from "./View";

const Comparison = ({
  project,
  onNext,
  onBack,
  setCurrentStatus,
  setDisplayProject,
}) => {
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const { t } = useTranslation(NAME_SPACES.ERRORS);
  const API_ERRORS = t("API", { returnObjects: true });
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [hideDetail, setHideDetail] = useState({});
  const [subscribeLoadingId, setSubscribeLoadingId] = useState(null);
  const [addSubscriptions] = useMutation(CREATE_SUBSCRIPTIONS);
  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION);
  const [duplicateProject] = useMutation(graphql.mutations.DUPLICATE_PROJECT);
  const [updateProject] = useMutation(UPDATE_PROJECT);

  const findStatus = (data, template) => {
    return find(data, ({ template: _ }) => _ === template);
  };
  const [getGeneralCosts, { data }] = useLazyQuery(GENERAL_COSTS, {
    onCompleted: () => setLoading(false),
  });
  const projectLocked = project.locked;
  const issuanceDate = get(project, "contract.issuanceDate");
  const retroactivityRules = buildRetroactivityPlanFilters({
    retroactive: project.retroactive,
    issuanceDate,
  });
  const retroactivityRulesKey = JSON.stringify(retroactivityRules);
  const selectedPlanKey = get(project, "fields.selectedToCompare", []).join(
    ",",
  );
  const signedSubscriptionId = get(
    find(
      get(project, "subscriptions", []),
      ({ status }) => status === SUBSCRIPTION_STATUSES.SIGNED,
    ),
    "id",
  );
  const [buildGeneralCosts] = useMutation(BUILD_GENERAL_COSTS, {
    variables: {
      data: {
        project: { id },
        insurancePlanFilter: {
          isIn: {
            id: get(project, "fields.selectedToCompare", []),
          },
          ...(retroactivityRules.length ? { rules: retroactivityRules } : {}),
        },
      },
    },
    onCompleted: ({ buildGeneralCosts }) => {
      const selectedInsurancePlans =
        intersection(
          buildGeneralCosts?.map(({ insurancePlan }) => insurancePlan?.id),
          get(project, "fields.selectedToCompare", []),
        ) || [];

      getGeneralCosts({
        variables: {
          where: { project: { id } },
          isIn: {
            insurancePlan: {
              id: selectedInsurancePlans,
            },
          },
        },
      });
    },
  });

  const duplicate = () => {
    const contactStatus = find(
      get(state, "tracer.flow.process.processStatuses"),
      ({ template }) =>
        template === TEMPLATES[PROCESS_TYPES.B2B].THEMES.DEFAULT.CONTACT,
    );
    duplicateProject({
      variables: {
        where: { id: project.id },
        data: {
          visible: true,
          statuses: [{ id: cuid(), status: { id: get(contactStatus, "id") } }],
        },
      },
      onCompleted: ({ duplicateProject }) => {
        navigate(
          `${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${duplicateProject.id}`,
          {
            state: {
              process: get(state, "tracer.flow.process"),
              tracer: get(state, "tracer"),
            },
          },
        );
      },
    });
  };

  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);

  const onDownload = async ({
    selectedData = [],
    payload = {},
    setState,
    setSelectedData,
  }) => {
    setState(MODAL_STATES.PENDING);
    const { errors } = await updateProject({
      variables: {
        where: { id: project.id },
        data: { fields: get(payload, "fields") },
      },
    });
    if (errors) {
      setState(MODAL_STATES.INITIAL);
      return;
    }
    return generateQuoteDocuments({
      variables: {
        data: {
          project: { id },
          insurancePlans: selectedData.map((id) => ({ id })),
        },
      },
      onCompleted: async ({ generateQuoteDocuments: { base64 } }) => {
        downloadFiles(base64);

        const finalizeDownload = () => {
          setState(MODAL_STATES.DONE);
          setTimeout(() => {
            setSelectedData([]);
            setState(MODAL_STATES.INITIAL);
            setOpenModal(false);
          }, 3000);

          onNext({ payload });
        };

        const existingPlanIds = get(project, "subscriptions", []).map(
          ({ insurancePlan }) => insurancePlan?.id,
        );
        const plansToSubscribe = selectedData.filter(
          (insurancePlanId) => !existingPlanIds.includes(insurancePlanId),
        );
        if (isEmpty(plansToSubscribe)) {
          return finalizeDownload();
        }

        addSubscriptions({
          variables: {
            data: plansToSubscribe.map((insurancePlanId) => ({
              id: cuid(),
              project: { id: project.id },
              insurancePlan: {
                id: insurancePlanId,
              },
            })),
          },
          onCompleted: ({ addSubscriptions: createdSubscriptions }) => {
            setDisplayProject((prev) => ({
              ...prev,
              subscriptions: [
                ...get(prev, "subscriptions", []),
                ...(createdSubscriptions || []).map((subscription) => ({
                  ...subscription,
                  status:
                    get(subscription, "status") || SUBSCRIPTION_STATUSES.DRAFT,
                })),
              ],
            }));
            finalizeDownload();
          },
        });
      },
      onError: () => {
        setTimeout(() => {
          notification.error({
            message: API_ERRORS.GENERIC,
          });
          setState(MODAL_STATES.INITIAL);
        }, 2000);
      },
    });
  };

  const onSubscribe = async ({ payload = {}, actionsTemplate }) => {
    const subscriptionId = actionsTemplate?.id;
    if (!subscriptionId || subscribeLoadingId) return;

    setSubscribeLoadingId(subscriptionId);
    // Let React paint the loading state before continuing.
    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      if (actionsTemplate?.status === SUBSCRIPTION_STATUSES.DRAFT) {
        await updateSubscription({
          variables: {
            where: { id: subscriptionId },
            data: { status: SUBSCRIPTION_STATUSES.IN_PROGRESS },
          },
        });
      }
      await onNext({
        payload: {
          ...payload,
          fields: {
            ...get(project, "fields", {}),
            ...get(payload, "fields", {}),
            currentSubscription:
              subscriptionId || get(project, "fields.currentSubscription"),
          },
        },
        actionsTemplate,
      });
    } catch (error) {
      setSubscribeLoadingId(null);
      throw error;
    }
  };

  const onContactSaved = (contact) => {
    if (isEmpty(contact)) return;
    setDisplayProject((prev) => ({
      ...prev,
      contact: omitDeep(contact, "__typename"),
    }));
  };

  const onSendQuote = async ({ payload = {} }) => {
    if (project.locked) {
      const status = findStatus(
        get(state, "tracer.flow.process.processStatuses"),
        TEMPLATES[PROCESS_TYPES.B2B].THEMES[TEMPLATE_TYPES.DEFAULT].QUOTE,
      );
      updateProject({
        variables: {
          where: { id: project.id },
          data: {
            fields: get(payload, "fields"),
          },
        },
      });
      setDisplayProject((prev) => ({
        ...prev,
        fields: get(payload, "fields"),
      }));
      return setCurrentStatus(status);
    }
    onNext({
      payload,
    });
  };

  const getGeneralCostsRef = useRef(getGeneralCosts);
  const buildGeneralCostsRef = useRef(buildGeneralCosts);
  getGeneralCostsRef.current = getGeneralCosts;
  buildGeneralCostsRef.current = buildGeneralCosts;

  useEffect(() => {
    const selectedPlanIds = selectedPlanKey ? selectedPlanKey.split(",") : [];
    if (signedSubscriptionId)
      navigate(
        `${SCREENS.PRIVATE.BROKER.SUBSCRIPTIONS.DETAIL.path}/${signedSubscriptionId}`,
      );
    if (projectLocked) {
      getGeneralCostsRef.current({
        variables: {
          where: {
            project: { id },
          },
          isIn: {
            insurancePlan: { id: selectedPlanIds },
          },
        },
      });
      return;
    }
    const rules = JSON.parse(retroactivityRulesKey);
    buildGeneralCostsRef.current({
      variables: {
        data: {
          project: { id },
          insurancePlanFilter: {
            isIn: { id: selectedPlanIds },
            ...(rules.length ? { rules } : {}),
          },
        },
      },
    });
  }, [
    id,
    navigate,
    projectLocked,
    retroactivityRulesKey,
    selectedPlanKey,
    signedSubscriptionId,
  ]);

  return (
    <>
      <ExpirationModal project={project} />
      <View
        project={project}
        onSubscribe={onSubscribe}
        subscribeLoadingId={subscribeLoadingId}
        onDownload={onDownload}
        onSendQuote={onSendQuote}
        onContactSaved={onContactSaved}
        onBack={onBack}
        data={get(data, "generalCosts.data", [])}
        loading={loading}
        openModal={openModal}
        duplicateProject={duplicate}
        setOpenModal={setOpenModal}
        hideDetail={hideDetail}
        setHideDetail={setHideDetail}
      />
    </>
  );
};

export default Comparison;
